import {
  Box,
  Button,
  Header,
  Link,
  Pagination,
  Select,
  SelectProps,
  SpaceBetween,
  Table,
} from '@cloudscape-design/components';
import { useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { PropertyFilterOption } from '@cloudscape-design/collection-hooks';

import { listChains } from '../../common/api/chains';
import { getPlaceMeta } from '../../common/api/places';
import { BUSINESS_TYPES } from '../../common/types';
import { DashboardViewLayout } from '../../components/DashboardViewLayout';
import { TextQueryFilter } from '../../components/TextQueryFilter';

const PAGE_SIZE = 50;

const ANY_BUSINESS_TYPE: SelectProps.Option = { label: 'Any business type', value: '' };
const ANY_INDUSTRY: SelectProps.Option = { label: 'Any industry', value: '' };

export default function ChainsListPage() {
  const navigate = useNavigate();
  const [textQuery, setTextQuery] = useState('');
  const [businessType, setBusinessType] = useState<SelectProps.Option>(ANY_BUSINESS_TYPE);
  const [industry, setIndustry] = useState<SelectProps.Option>(ANY_INDUSTRY);
  const [page, setPage] = useState(1);

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: [
      'chains',
      page,
      PAGE_SIZE,
      textQuery,
      businessType.value ?? '',
      industry.value ?? '',
    ],
    queryFn: () =>
      listChains(page, PAGE_SIZE, {
        q: textQuery || undefined,
        business_type: businessType.value || undefined,
        industry: industry.value || undefined,
      }),
  });

  // Industry options come from the same distinct place_type source the main
  // Places table uses (raw PlaceType names as values, prettified labels).
  const metaQuery = useQuery({
    queryKey: ['places', 'meta'],
    queryFn: getPlaceMeta,
    staleTime: 360_000,
  });
  const industryOptions: SelectProps.Option[] = useMemo(() => {
    const opts = ((metaQuery.data?.filteringOptions ?? []) as PropertyFilterOption[])
      .filter((o) => o.propertyKey === 'place_type')
      .map((o) => ({ label: o.label ?? o.value, value: o.value }));
    return [ANY_INDUSTRY, ...opts];
  }, [metaQuery.data]);

  const businessTypeOptions: SelectProps.Option[] = useMemo(
    () => [
      ANY_BUSINESS_TYPE,
      ...BUSINESS_TYPES.filter((o) => o.value !== 'Other'),
    ],
    [],
  );

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const pages = data?.pages ?? 1;

  return (
    <DashboardViewLayout
      breadcrumbs={[{ text: 'Chains', href: '/dashboard/chains' }]}
      contentType="table"
    >
      <Table
        variant="full-page"
        stickyHeader
        loading={isLoading}
        loadingText="Loading chains…"
        items={items}
        pagination={
          <Pagination
            currentPageIndex={page}
            pagesCount={pages}
            disabled={isLoading}
            onChange={({ detail }) => setPage(detail.currentPageIndex)}
          />
        }
        filter={
          <SpaceBetween direction="horizontal" size="xs">
            <TextQueryFilter
              loading={isLoading}
              text={textQuery}
              setText={(q) => {
                setTextQuery(q);
                setPage(1);
              }}
            />
            <Select
              selectedOption={businessType}
              options={businessTypeOptions}
              onChange={({ detail }) => {
                setBusinessType(detail.selectedOption);
                setPage(1);
              }}
              ariaLabel="Filter by business type"
            />
            <Select
              selectedOption={industry}
              options={industryOptions}
              statusType={metaQuery.isLoading ? 'loading' : 'finished'}
              filteringType="auto"
              onChange={({ detail }) => {
                setIndustry(detail.selectedOption);
                setPage(1);
              }}
              ariaLabel="Filter by industry"
            />
          </SpaceBetween>
        }
        empty={
          <Box color="inherit" margin={{ vertical: 'xs' }} textAlign="center">
            {error ? (
              <SpaceBetween size="xxs">
                <div>
                  <b>Failed to load chains</b>
                  <Box color="inherit" variant="p">{String(error)}</Box>
                </div>
                <Button onClick={() => refetch()}>Retry</Button>
              </SpaceBetween>
            ) : textQuery || businessType.value || industry.value ? (
              <SpaceBetween size="xxs">
                <div>
                  <b>No matches</b>
                  <Box color="inherit" variant="p">
                    No chains match the current search/filters.
                  </Box>
                </div>
              </SpaceBetween>
            ) : (
              <SpaceBetween size="xxs">
                <div>
                  <b>No chains yet</b>
                  <Box color="inherit" variant="p">
                    Chains are created when you run a bulk chain search from
                    the Scorecard → Create page (Chain tab).
                  </Box>
                </div>
                <Button
                  variant="primary"
                  onClick={() =>
                    navigate({
                      to: '/dashboard/scorecard/create',
                      search: { tab: 'chain' },
                    })
                  }
                >
                  Run a chain search
                </Button>
              </SpaceBetween>
            )}
          </Box>
        }
        header={
          <Header
            variant="awsui-h1-sticky"
            counter={`(${total})`}
            description="Groups of Place locations that belong to the same brand. Chains are created automatically by chain searches; Places get linked via auto-match (exact name) or via the Suggested Links tab on each chain. Each chain's discovery searches are listed on its Searches tab."
            actions={
              <SpaceBetween size="xs" direction="horizontal">
                <Button onClick={() => refetch()} loading={isFetching} iconName="refresh" />
                <Button
                  variant="primary"
                  onClick={() =>
                    navigate({
                      to: '/dashboard/scorecard/create',
                      search: { tab: 'chain' },
                    })
                  }
                >
                  New chain search
                </Button>
              </SpaceBetween>
            }
          >
            Chains
          </Header>
        }
        columnDefinitions={[
          {
            id: 'name',
            header: 'Chain',
            cell: (c) => (
              <Link
                href={`/dashboard/chains/${c.uuid}`}
                onFollow={(e) => {
                  e.preventDefault();
                  navigate({
                    to: '/dashboard/chains/$chainUuid',
                    params: { chainUuid: c.uuid },
                  });
                }}
              >
                {c.name}
              </Link>
            ),
            minWidth: 240,
          },
          {
            id: 'slug',
            header: 'Slug',
            cell: (c) => <Box variant="code">{c.slug}</Box>,
            width: 200,
          },
          {
            id: 'location_count',
            header: 'Locations',
            cell: (c) => c.location_count,
            width: 110,
          },
          {
            id: 'created',
            header: 'Created',
            cell: (c) => new Date(c.created_at).toLocaleString(),
            width: 200,
          },
      ]}
      />
    </DashboardViewLayout>
  );
}
